import React, { memo, useMemo } from 'react';
import classNames from 'classnames';
import { Box } from '@mui/material';

import { SurfedDirection } from 'types/recordings';
import { WaveGraphBar } from 'utils/rewinds/waveInsightData';
import type { RewindError } from 'utils/rewinds/playlistBlocks.types';
import { oneMinute } from 'utils/time';

import { useRewindGraphGridParams } from '../hooks/useRewindGraphGridParams';
import RewindGraphYAxis, {
  calculateAxisLabels,
  calculateMaxWaveHeight,
} from '../SharedGraphComponents/RewindGraphHeightAxis';
import RewindGraphXAxis from '../SharedGraphComponents/RewindGraphTimeAxis';
import SmartRewindsGraphHeader from '../SharedGraphComponents/WaveInsightsGraphHeader';
import { RewindGraphErrors } from '../SharedGraphComponents/RewindGraphErrors';
import OutOfRange from './components/OutOfRange';

import styles from './WaveBarGraph.module.scss';

const TICK_FREQUENCY_MS = 5 * oneMinute;

const Bar = ({
  graphBar,
  isHighlighted,
  barStyles,
}: {
  graphBar: WaveGraphBar | any;
  isHighlighted: boolean;
  barStyles: React.CSSProperties;
}) => (
  <Box
    data-testid="wave-insights-graph-bar"
    style={barStyles}
    className={classNames(
      styles.waveBarGraph,
      styles.waveBarGraphNone,
      graphBar.surfedDirection === SurfedDirection.LEFT && styles.waveBarGraphSurfed,
      graphBar.surfedDirection === SurfedDirection.RIGHT && styles.waveBarGraphSurfed,
      graphBar.surfedDirection === SurfedDirection.BOTH && styles.waveBarGraphSurfed,
      isHighlighted && styles.waveBarGraphHighlighted,
    )}
  />
);

const GraphBar = memo(Bar);

const Bars = ({
  waveGraphBars,
  highlightedBarIndex,
  roundedMaxWaveHeight,
  maxBarHeightPercentage,
  graphGridParameters,
}: {
  waveGraphBars: WaveGraphBar[];
  highlightedBarIndex: number;
  roundedMaxWaveHeight: number;
  maxBarHeightPercentage: number;
  graphGridParameters: any;
}) => (
  <>
    {waveGraphBars.map((graphBar: any, index: number) => {
      if ('startTime' in graphBar && 'height' in graphBar) {
        return (
          <GraphBar
            data-testid="wave-insights-graph-bar"
            key={`graphBar-${graphBar.startTime}`}
            graphBar={graphBar}
            isHighlighted={index === highlightedBarIndex}
            barStyles={{
              gridColumn: graphGridParameters.chartStartColumn + index,
              height: `${Math.floor(
                ((graphBar.height || 0) / roundedMaxWaveHeight) * maxBarHeightPercentage,
              )}%`,
              gridRow: graphGridParameters.chartAreaRows,
            }}
          />
        );
      }
      return null;
    })}
  </>
);

const GraphBars = memo(Bars);

interface Props {
  bucketEndTimeInMs: number;
  bucketStartTimeInMs: number;
  errors: RewindError[];
  highlightedBarIndex: number;
  isOutOfRange: boolean;
  outOfRangeClick?: () => void;
  supportsSmartRewinds?: boolean;
  timezone: string;
  waveGraphBars: WaveGraphBar[];
  waveUnits: string;
  isFullscreen?: boolean;
}

const WaveBarGraph: React.FC<Props> = ({
  bucketEndTimeInMs,
  bucketStartTimeInMs,
  errors,
  highlightedBarIndex,
  isOutOfRange,
  outOfRangeClick,
  supportsSmartRewinds,
  timezone,
  waveGraphBars,
  waveUnits,
  isFullscreen,
}) => {
  const graphGridParameters = useRewindGraphGridParams({
    chartAreaColumnsLength: waveGraphBars.length,
    renderDataRow: supportsSmartRewinds,
  });

  const roundedMaxWaveHeight = useMemo(() => {
    const maxHeight = waveGraphBars.reduce((maxHeightAccum: number, wave): number => {
      if ('height' in wave && wave.height > maxHeightAccum) return wave.height;
      return maxHeightAccum;
    }, 0);
    return calculateMaxWaveHeight(maxHeight, waveUnits);
  }, [waveGraphBars, waveUnits]);

  const hasInsightsData = supportsSmartRewinds && roundedMaxWaveHeight > 0;

  const maxBarHeightPercentage = useMemo(() => {
    const axisLength = calculateAxisLabels(roundedMaxWaveHeight, waveUnits).length;
    return ((axisLength * 2) / (axisLength * 2 + 1)) * 100;
  }, [roundedMaxWaveHeight, waveUnits]);

  const height = hasInsightsData ? graphGridParameters.fullHeight : graphGridParameters.baseHeight;
  const heightWithErrors = errors.length
    ? Math.max(height, graphGridParameters.baseHeight + graphGridParameters.errorHeight)
    : height;

  return (
    <Box>
      {supportsSmartRewinds && (
        <SmartRewindsGraphHeader
          hasInsightsData={roundedMaxWaveHeight > 0}
          showSmartCamIcon
          showLegend={!isOutOfRange}
          isFullscreen={isFullscreen}
        />
      )}
      {isOutOfRange && (
        <OutOfRange
          bucketStartTimeInMs={bucketStartTimeInMs}
          onClick={outOfRangeClick}
          timezone={timezone}
        />
      )}
      <Box
        className={styles.waveInsightsGraphGridContainer}
        gridTemplateRows={graphGridParameters.gridTemplateRows}
        gridTemplateColumns={graphGridParameters.gridTemplateColumns}
        height={heightWithErrors}
      >
        <RewindGraphXAxis
          bucketEndTimeInMs={bucketEndTimeInMs}
          bucketStartTimeInMs={bucketStartTimeInMs}
          tickSpacingInMs={TICK_FREQUENCY_MS}
          timezone={timezone}
          graphGridParameters={graphGridParameters}
        />
        {!isOutOfRange && (
          <>
            {hasInsightsData && (
              <RewindGraphYAxis
                roundedMaxWaveHeight={roundedMaxWaveHeight}
                waveUnits={waveUnits}
                graphGridParameters={graphGridParameters}
              />
            )}
            {hasInsightsData && (
              <GraphBars
                graphGridParameters={graphGridParameters}
                highlightedBarIndex={highlightedBarIndex}
                maxBarHeightPercentage={maxBarHeightPercentage}
                roundedMaxWaveHeight={roundedMaxWaveHeight}
                waveGraphBars={waveGraphBars}
              />
            )}
            <RewindGraphErrors
              bucketStartTimeInMs={bucketStartTimeInMs}
              waveGraphBars={waveGraphBars}
              graphGridParameters={graphGridParameters}
              errors={supportsSmartRewinds ? errors : []}
            />
          </>
        )}
      </Box>
    </Box>
  );
};

export default memo(WaveBarGraph);
